(ns arthur.domain.bring
  "Bringing symbols into a clip from another: out of a saved project, or out of
  a freeze of new footage.

  Copied, never linked. What comes in gets ids of its own where they are taken,
  and editing it here does not touch where it came from. Tracking identities and
  the analysis they were measured by come along only when the receiving clip can
  hold them; otherwise what comes in is drawing, which plays but does not re-tune.

  Plain data in and out — documents, and in `placed` a document with its store —
  so the events that fetch them are only fetching."
  (:refer-clojure :exclude [take])
  (:require [arthur.domain.clip :as clip]
            [arthur.domain.feature :as feature]
            [arthur.domain.node :as node]
            [arthur.domain.retime :as retime]
            [clojure.string :as string]))

(defn symbols
  "Copy symbols `roots` of clip `other`, and every symbol they place, into
  `clip`. Returns `{:clip :ids}`, where `:ids` maps each copied symbol's id in
  `other` to its id here.

  AN ID THAT IS TAKEN IS RENAMED, never merged: two symbols that happen to share
  an id are two drawings, and a cel's `:source :symbol` inside the copy is
  rewritten to follow. `wanted` maps a root's id in `other` to the id it should preferably get,
  which is how a symbol made from footage is called what the person typed rather
  than `:main`.

  Only symbols travel. What else `other` holds — tracking identities, an analysis
  — is the caller's decision, because whether it can come too depends on what
  `clip` already has.

  WHAT COMES IN KEEPS ITS TIMING. `other` is moved onto this document's rate
  first, with `retime/scale-spans`, so a take measured at 30 plays in seconds
  here as it did there; footage and measurements keep their own frames and the
  placements of them take up the difference."
  [clip other roots wanted]
  (let [other (cond-> other
                (and (:fps clip) (:fps other) (not= (:fps clip) (:fps other)))
                (retime/scale-spans (:fps clip)))
        ;; A tree walk is safe because placing cannot make a cycle.
        reach (into #{} (mapcat #(tree-seq any? (partial clip/places other) %)) roots)
        ids (reduce (fn [ids sid]
                      (let [taken? #(or (contains? (:symbols clip) %)
                                        (some #{%} (vals ids)))]
                        (assoc ids sid (clip/free-id taken? (get wanted sid sid)))))
                    {} (sort-by str reach))
        ;; Cel identity and timing stay put; content references follow
        ;; the symbol IDs assigned in the destination document.
        repoint (fn [n ids]
                  (if (node/source n)
                    (update-in n [:source :symbol] ids)
                    n))
        copy (fn [sid]
               (-> (clip/symbol other sid)
                   (assoc :id (ids sid))
                   (update :nodes #(into {} (map (fn [[id n]] [id (repoint n ids)])) %))))]
    {:clip (reduce (fn [c sid] (assoc-in c [:symbols (ids sid)] (copy sid))) clip reach)
     :ids ids}))

(defn symbol-id
  "An id for a symbol a person has named: the name, lower-cased and hyphenated,
  or `:symbol` when nothing of it survives."
  [label]
  (let [slug (-> (str label) string/lower-case
                 (string/replace #"[^a-z0-9]+" "-")
                 (string/replace #"^-+|-+$" ""))]
    (keyword (if (seq slug) slug "symbol"))))

(defn take
  "Put `frozen`, a take, into `clip` as ONE symbol called `label`. Returns the
  changed clip, the imported symbol id and the old-to-scoped subject ids.

  `frozen` is what `flow/freeze/clip` makes: a `:main` that places one symbol per
  tracked face. `:main` becomes the named symbol — it is what holds the faces in
  stage pixels, so it is the thing worth placing. Each generated face symbol gets
  an automatic association with the take's SOUND, source frames `range` of
  footage `footage-id`. Thus the take is heard through the faces it places, and
  a face subsequently placed by itself still brings its sound. This is the same
  shape a future manual symbol/sound association can write; dropping a face is
  not a special operation.

  A multi-face take consequently reaches the same recording through several
  symbols. `nest/audio-tracks` collapses simultaneous copies carrying the same
  `:media-link`; placing those faces at different times still schedules each one.

  The unique imported symbol id scopes every subject id. Thus two takes may both
  arrive with detector subject `:face-1` without colliding in the document."
  [clip frozen label footage-id range]
  (let [scope (fn [root subject] (keyword (str (name root) "." (name subject))))
        root (clip/free-id
              (fn [candidate]
                (or (contains? (:symbols clip) candidate)
                    (some #(contains? (:symbols clip) (scope candidate %))
                          (keys (:subjects frozen)))))
              (symbol-id label))
        scoped (partial scope root)
        wanted (into {:main root :footage (scoped :footage)}
                     (map (fn [subject] [subject (scoped subject)]))
                     (keys (:subjects frozen)))
        sound    {:id :sound :name "sound" :kind :audio :parent nil
                  :z "z-sound" :source {:footage footage-id :fps (:fps frozen)}
                  ;; All automatic copies name one recording. The audio walk
                  ;; uses this identity only to avoid mixing that recording once
                  ;; per detected face when the complete take is played.
                  :media-link [:footage footage-id range]
                  ;; Source frame `start` plays on the symbol's 0.
                  :span range
                  :time {:mode :map :at (- (first range)) :rate 1}}
        ;; Into the faces BEFORE they are copied, so the sound is moved onto this
        ;; document's rate along with the rest of them.
        frozen   (reduce (fn [document face]
                           (assoc-in document [:symbols face :nodes :sound] sound))
                         frozen (keys (:subjects frozen)))
        {c :clip ids :ids} (symbols clip frozen [:main] wanted)
        sid      (ids :main)
        feature-ids (into {}
                          (map (fn [[id f]]
                                 [id (feature/owned (ids (:subject f)) (keyword (name id)))]))
                          (:features frozen))
        subjects (into {}
                       (map (fn [[id subject]]
                              (let [new-id (ids id)]
                                [new-id (assoc subject :id new-id
                                                       :footage footage-id)])))
                       (:subjects frozen))
        features (into {}
                       (map (fn [[id f]]
                              (let [new-id (feature-ids id)]
                                [new-id (-> f
                                            (assoc :id new-id
                                                   :subject (ids (:subject f))
                                                   :symbol (ids (:symbol f))))])))
                       (:features frozen))
        groups (into {}
                     (map (fn [[id g]]
                            (let [new-subject (ids (:subject g))
                                  new-id (feature/owned new-subject (keyword (name id)))]
                              [new-id (-> g
                                          (assoc :id new-id :subject new-subject)
                                          (update :members #(mapv feature-ids %)))])))
                     (:groups frozen))]
    {:sid sid
     :subject-ids (select-keys ids (keys (:subjects frozen)))
     :clip (-> c
               (assoc-in [:symbols sid :name] (str label))
               (update :analyses merge (:analyses frozen))
               (update :subjects merge subjects)
               (update :features merge features)
               (update :groups merge groups))}))


(defn placed
  "`entry` — a document and its store — once `brought` holds the symbols brought
  in and `store` their blocks: the stores merged and an instance of `sid` placed
  in `host` at `frame`, its middle on stage pixel `point` or where it was drawn
  when there is none. See `clip/place-symbol`."
  [entry brought store sid host frame uuid point]
  (let [st (merge (:store entry) store)]
    (assoc entry :store st :clip (clip/place-symbol brought st host sid frame uuid point))))
